import type { FC, PropsWithChildren } from "react"
import { useMemo, useReducer, useState } from "react"
import fp from "lodash/fp"
import { Responsive, WidthProvider } from "react-grid-layout"

import type { QueryPlotDef } from "types/dashboard.types"
import type { Device } from "types/device.types"

import { MD_ROW_HEIGHT, GRID_LAYOUT_GAP, SM_ROW_HEIGHT } from "helpers/utils/constants"
import { layoutReducer } from "helpers/reducers/dashboardReducers"
import { getLayout, sortPlotDefsByLayout } from "helpers/utils/dashboards"
import Message from "widgets/common/Message"
import HoverSyncProvider from "helpers/contexts/HoverSyncProvider"
import DashboardContext from "helpers/contexts/DashboardContext"
import useDateRange from "helpers/hooks/useDateRange"
import CrossEntityPlot from "widgets/plots/CrossEntityPlot"
import { useDeviceLocationData } from "widgets/plots/geo/useDeviceLocationData"

const GridLayout = WidthProvider(Responsive)

interface DeviceDashboardProps {
  device: Device
  config: {
    type: string
    plotDefs: QueryPlotDef[]
  }
}

const DeviceDashboard: FC<PropsWithChildren<DeviceDashboardProps>> = ({
  device,
  config,
  children,
}) => {
  const { arePlotsLoading } = useDateRange()
  const initialLayout = getLayout(config.type)
  //TODO: Once we have more layouts for the different media queries, we'll have to take that into account for the initial layout
  const [plotLayout, dispatchPlotLayout] = useReducer(layoutReducer, initialLayout)
  const plotDefs = useMemo(
    () => sortPlotDefsByLayout(config.plotDefs, initialLayout),
    [config.plotDefs, initialLayout],
  )
  const layout = useMemo(() => fp.values(plotLayout), [plotLayout])

  const [rowHeight, setRowHeight] = useState(MD_ROW_HEIGHT)

  // Only consider plots that are actually rendered. Some layouts (e.g. the tower
  // tab) define cells for plots that are conditionally dropped from `plotDefs`;
  // those orphaned entries never collapse to h: 0, so checking the full layout
  // would keep `isData` permanently true and suppress the no-data message.
  const isData = config.plotDefs.some(
    (def) => (plotLayout[def.view.nameKey]?.h ?? 0) !== 0,
  )

  // The device tab renders the location map alongside the grid; don't show the
  // grid's "no data" message when the map itself has data to display.
  const { hasLocationData } = useDeviceLocationData(device.id)
  const hideNoDataMessage = config.type === "deviceTab" && hasLocationData

  return (
    <DashboardContext.Provider
      value={{
        config,
        rowHeight,
        entityType: "device" as const,
        plotLayout,
        dispatchPlotLayout,
      }}
    >
      <HoverSyncProvider>
        {children}
        <GridLayout
          style={{
            position: "relative",
            marginRight: `-${GRID_LAYOUT_GAP}px`,
            marginLeft: `-${GRID_LAYOUT_GAP}px`,
          }}
          layouts={{ lg: layout }}
          breakpoints={{
            lg: 1000,
            md: 800,
            sm: 650,
          }}
          cols={{ lg: 12, md: 6, sm: 3 }}
          margin={[GRID_LAYOUT_GAP, GRID_LAYOUT_GAP]}
          rowHeight={rowHeight}
          draggableHandle=".dragIcon"
          onBreakpointChange={(breakpoint: string) =>
            breakpoint === "sm"
              ? setRowHeight(SM_ROW_HEIGHT)
              : setRowHeight(MD_ROW_HEIGHT)
          }
        >
          {plotDefs.map((def) => {
            return (
              <div key={def.view.nameKey}>
                <CrossEntityPlot plotDef={def} contextEntity={device} />
              </div>
            )
          })}
        </GridLayout>
      </HoverSyncProvider>
      {!arePlotsLoading && !isData && !hideNoDataMessage && (
        <Message messageKey={"generic.NO_DATA_IN_TIME_WINDOW"} />
      )}
    </DashboardContext.Provider>
  )
}

export default DeviceDashboard
